import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  IconButton,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useClientBoardTasks } from "../../hooks/useTeamBoards";
import { moveTeamBoardCard } from "../../api/client";
import TeamTasksLoadError from "./components/TeamTasksLoadError";
import TaskCardModalContainer from "./components/TaskCardModalContainer";
import { TeamTasksFeedbackProvider } from "./components/TeamTasksFeedbackProvider";
import BoardColumn from "./components/BoardColumn";
import { useTeamBoardAction } from "./hooks/useTeamBoardAction";

function columnKeyOf(task) {
  if (task?.columnKey) return String(task.columnKey);
  return `${task?.columnName || "Sem coluna"}::${Number(Boolean(task?.columnIsDone))}::${Number(
    Boolean(task?.columnIsWaiting)
  )}`;
}

function resolvePhysicalColumnId(boardColumnsByBoardId, boardId, virtualColumn) {
  const cols = boardColumnsByBoardId?.[Number(boardId)] || [];
  if (!cols.length || !virtualColumn) return null;
  const byName = cols.find(
    (c) => String(c.name).trim().toLowerCase() === String(virtualColumn.name).trim().toLowerCase()
  );
  if (byName) return Number(byName.id);
  const byFlags = cols.find(
    (c) =>
      Boolean(c.isDone) === Boolean(virtualColumn.isDone) &&
      Boolean(c.isWaiting) === Boolean(virtualColumn.isWaiting)
  );
  return byFlags ? Number(byFlags.id) : null;
}

function buildColumns(apiColumns, tasks) {
  const base =
    Array.isArray(apiColumns) && apiColumns.length
      ? apiColumns
      : [
          { id: "A fazer::0::0", name: "A fazer", isDone: false, isWaiting: false, position: 0 },
          {
            id: "Em andamento::0::0",
            name: "Em andamento",
            isDone: false,
            isWaiting: false,
            position: 1,
          },
          {
            id: "Aguardando equipe::0::0",
            name: "Aguardando equipe",
            isDone: false,
            isWaiting: false,
            position: 2,
          },
          {
            id: "Aguardando cliente::0::1",
            name: "Aguardando cliente",
            isDone: false,
            isWaiting: true,
            position: 3,
          },
        ];

  const byKey = new Map(
    base.map((col, index) => [
      String(col.id),
      {
        ...col,
        id: String(col.id),
        position: col.position != null ? col.position : index,
        cards: [],
      },
    ])
  );

  for (const task of tasks || []) {
    const key = columnKeyOf(task);
    if (!byKey.has(key)) {
      byKey.set(key, {
        id: key,
        name: task.columnName || "Outros",
        isDone: Boolean(task.columnIsDone),
        isWaiting: Boolean(task.columnIsWaiting),
        position: byKey.size,
        cards: [],
      });
    }
    byKey.get(key).cards.push({
      ...task,
      // TaskCard espera título interno; no agregado priorizamos o título operacional
      title: task.title || task.clientTitle,
      clientName: task.clientName,
    });
  }

  return Array.from(byKey.values()).sort(
    (a, b) => (a.position || 0) - (b.position || 0) || String(a.name).localeCompare(String(b.name))
  );
}

function applyAggregateMove(columns, { cardId, sourceColumnId, destColumnId, destIndex }) {
  const next = columns.map((col) => ({
    ...col,
    cards: [...(col.cards || [])],
  }));
  const sourceCol = next.find((c) => String(c.id) === String(sourceColumnId));
  const destCol = next.find((c) => String(c.id) === String(destColumnId));
  if (!sourceCol || !destCol) return columns;

  const fromIdx = sourceCol.cards.findIndex((c) => Number(c.id) === Number(cardId));
  if (fromIdx < 0) return columns;

  const [moved] = sourceCol.cards.splice(fromIdx, 1);
  let insertAt = destIndex;
  if (String(sourceColumnId) === String(destColumnId) && fromIdx < destIndex) {
    insertAt = destIndex - 1;
  }
  destCol.cards.splice(insertAt, 0, {
    ...moved,
    columnKey: String(destColumnId),
    columnName: destCol.name,
    columnIsDone: Boolean(destCol.isDone),
    columnIsWaiting: Boolean(destCol.isWaiting),
  });
  return next;
}

const ClientTasksAggregatePageInner = ({ standalone = false, basePath = "/equipe/tarefas" }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useTeamBoardAction();
  const [status, setStatus] = useState("open");
  const [selectedTask, setSelectedTask] = useState(null);
  const [localColumns, setLocalColumns] = useState(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useClientBoardTasks({ status });

  const boardColumnsByBoardId = data?.boardColumnsByBoardId || {};
  const serverColumns = useMemo(
    () => buildColumns(data?.columns, data?.tasks || []),
    [data?.columns, data?.tasks]
  );

  useEffect(() => {
    setLocalColumns(null);
  }, [data?.tasks, data?.columns, status]);

  const columns = localColumns || serverColumns;
  const openCount = useMemo(
    () => (data?.tasks || []).filter((t) => !t.columnIsDone && !t.completedAt).length,
    [data?.tasks]
  );
  const totalCount = (data?.tasks || []).length;

  const onDragEnd = useCallback(
    (result) => {
      const { destination, source, draggableId } = result;
      if (!destination) return;
      if (
        String(source.droppableId) === String(destination.droppableId) &&
        source.index === destination.index
      ) {
        return;
      }

      const cardId = Number(draggableId);
      const sourceColumnId = String(source.droppableId);
      const destColumnId = String(destination.droppableId);
      const destIndex = destination.index;

      const sourceCol = columns.find((c) => String(c.id) === sourceColumnId);
      const destCol = columns.find((c) => String(c.id) === destColumnId);
      const moving = (sourceCol?.cards || []).find((c) => Number(c.id) === cardId);
      if (!moving || !destCol) return;

      const boardId = Number(moving.boardId);
      const physicalColumnId = resolvePhysicalColumnId(boardColumnsByBoardId, boardId, destCol);
      if (!physicalColumnId) {
        showError(
          new Error("Coluna correspondente não encontrada no painel deste cliente."),
          "moveCard"
        );
        return;
      }

      const optimistic = applyAggregateMove(columns, {
        cardId,
        sourceColumnId,
        destColumnId,
        destIndex,
      });
      setLocalColumns(optimistic);

      const destCardsAfter = (optimistic.find((c) => String(c.id) === destColumnId)?.cards || [])
        .filter((c) => Number(c.boardId) === boardId);
      const position = destCardsAfter.findIndex((c) => Number(c.id) === cardId);

      moveTeamBoardCard(boardId, cardId, {
        columnId: physicalColumnId,
        position: position >= 0 ? position : destIndex,
      })
        .then(() => {
          queryClient.invalidateQueries(["teamClientBoardTasks"]);
          queryClient.invalidateQueries(["teamBoard", boardId]);
        })
        .catch((err) => {
          setLocalColumns(null);
          showError(err, "moveCard");
          refetch();
        });
    },
    [boardColumnsByBoardId, columns, queryClient, refetch, showError]
  );

  const handleModalClose = () => {
    setSelectedTask(null);
    queryClient.invalidateQueries(["teamClientBoardTasks"]);
    refetch();
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor:
          theme.palette.mode === "dark"
            ? "background.default"
            : alpha(theme.palette.grey[50], 0.9),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          spacing={1.5}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
            <IconButton onClick={() => navigate(basePath)} aria-label="Voltar" size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: { xs: "none", sm: "grid" },
                placeItems: "center",
                bgcolor: alpha(theme.palette.info.main, 0.12),
                color: "info.main",
                flexShrink: 0,
              }}
            >
              <StorefrontOutlinedIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h6" fontWeight={800} noWrap>
                  Tarefas de clientes
                </Typography>
                <Chip
                  size="small"
                  label={t("clientTasksAggregateBadge")}
                  color="info"
                  sx={{ fontWeight: 800, height: 22 }}
                />
                <Chip
                  size="small"
                  label={t("clientTasksAggregateWorkflowBadge")}
                  variant="outlined"
                  sx={{ fontWeight: 700, height: 22 }}
                />
                <Chip
                  size="small"
                  label={`${openCount} aberta${openCount === 1 ? "" : "s"}`}
                  color="info"
                  variant="outlined"
                  sx={{ fontWeight: 800, height: 22 }}
                />
                {status === "all" ? (
                  <Chip
                    size="small"
                    label={`${totalCount} no total`}
                    variant="outlined"
                    sx={{ fontWeight: 700, height: 22 }}
                  />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t("clientTasksAggregatePageHint")}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant={status === "open" ? "contained" : "outlined"}
              onClick={() => setStatus("open")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Abertas
            </Button>
            <Button
              size="small"
              variant={status === "all" ? "contained" : "outlined"}
              onClick={() => setStatus("all")}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Todas
            </Button>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Atualizar"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {isError ? (
        <Box sx={{ px: 2.5, pt: 2 }}>
          <TeamTasksLoadError error={error} onRetry={() => refetch()} isRetrying={isFetching} />
        </Box>
      ) : null}

      {isLoading ? (
        <Box sx={{ display: "grid", placeItems: "center", flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!isLoading && !isError ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="client-tasks-columns" direction="horizontal" type="COLUMN">
            {(columnsProvided) => (
              <Box
                ref={columnsProvided.innerRef}
                {...columnsProvided.droppableProps}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  overflowX: "auto",
                  overflowY: "hidden",
                  flex: 1,
                  minHeight: 0,
                  px: { xs: 1.5, sm: 2.5 },
                  py: 1.75,
                  alignItems: "stretch",
                  WebkitOverflowScrolling: "touch",
                  backgroundImage:
                    theme.palette.mode === "dark"
                      ? "none"
                      : `radial-gradient(${alpha(theme.palette.primary.main, 0.035)} 1px, transparent 1px)`,
                  backgroundSize: "20px 20px",
                  "&::-webkit-scrollbar": { height: 8 },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: alpha(theme.palette.text.primary, 0.16),
                    borderRadius: 999,
                  },
                }}
              >
                {columns.map((col, colIndex) => (
                  <Draggable
                    key={col.id}
                    draggableId={`column-${col.id}`}
                    index={colIndex}
                    isDragDisabled
                  >
                    {(colProvided) => (
                      <Box
                        ref={colProvided.innerRef}
                        {...colProvided.draggableProps}
                        sx={{ height: "100%" }}
                      >
                        <BoardColumn
                          column={col}
                          canManage={false}
                          canCreate={false}
                          onOpenCard={(card) =>
                            setSelectedTask({
                              ...card,
                              boardId: card.boardId,
                            })
                          }
                          onAddCard={undefined}
                          onUpdateColumn={undefined}
                          onDeleteColumn={undefined}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {columnsProvided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      ) : null}

      {selectedTask ? (
        <TaskCardModalContainer
          open={Boolean(selectedTask)}
          card={selectedTask}
          boardId={selectedTask.boardId}
          onClose={handleModalClose}
          invalidateInboxOnClose
        />
      ) : null}
    </Box>
  );
};

const ClientTasksAggregatePage = (props) => (
  <TeamTasksFeedbackProvider>
    <ClientTasksAggregatePageInner {...props} />
  </TeamTasksFeedbackProvider>
);

export default ClientTasksAggregatePage;
